import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';

import { ApiConfiguration } from '../../shared/api';
import { AuthService, REFRESH_TOKEN_NAME, TOKEN_NAME } from './auth.service';

/**
 * Holds a single browser-wide SSE connection while the user is signed in and
 * listens for `force-logout` events pushed when an admin disables the account.
 * On receipt it wipes credentials and bounces the user to the login page so
 * they cannot keep operating with a stale token.
 */
@Injectable({ providedIn: 'root' })
export class SessionMonitorService {
  private authService = inject(AuthService);
  private router = inject(Router);
  private apiConfig = inject(ApiConfiguration);
  private zone = inject(NgZone);

  private eventSource: EventSource | null = null;
  private currentToken: string | null = null;

  /**
   * Open (or reopen) the SSE connection for the currently stored token. Safe
   * to call repeatedly — if the token hasn't changed, the existing connection
   * is reused.
   */
  start(): void {
    const token = this.authService.getToken();
    if (!token) {
      this.stop();
      return;
    }

    if (this.eventSource && this.currentToken === token) {
      return;
    }

    this.stop();
    this.currentToken = token;

    const url = `https://ia-dev-dashboard-api.transflo.com/api/Sse/task-updates?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.addEventListener('force-logout', () => {
      this.zone.run(() => this.handleForceLogout());
    });

    es.onerror = () => {
      // EventSource auto-reconnects. If the server closed the stream because
      // the user was disabled mid-session, the next reconnect will be rejected
      // by force-logout-on-connect logic on the server side.
    };

    this.eventSource = es;
  }

  stop(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.currentToken = null;
  }

  private handleForceLogout(): void {
    this.stop();
    localStorage.removeItem(TOKEN_NAME);
    localStorage.removeItem(REFRESH_TOKEN_NAME);
    localStorage.removeItem('Permission');
    this.router.navigate(['/Login'], { state: { disabled: true } });
  }
}
